import { Children, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { QESAlertProps } from "./Alert";

export interface QESAlertsContainerProps {
  children: ReactNode;
  /**
   * A string that will be appended to the container's className
   */
  containerClass?: string;
}

let timer: number;

/**
 * This serves as a wrapper to add collapsing functionality to a set of Alert components.<br>
 * <br>
 * Our design guide dictates that alerts should be ordered by highest severity to lowest, and "danger" alerts are not collapsed. This component consciously does not make any effort to sort those for you, leaving that up to the developer. <br>
 * <br>
 * *Known issues: Uncollapsed collapsible alerts throw off the collapse/expand animation. Deleted alerts throw off the spacing.*
 */
export const QESAlertsContainer: React.FC<React.PropsWithChildren<QESAlertsContainerProps>> = (props: QESAlertsContainerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    calculateSpacing();
  }, [props.children]);

  useEffect(() => {
    if (isOpen) {
      const fullHeight = Array.from(containerRef.current.children).reduce((total: number, child) => {
        // plus 8 because each alert (except the first) will have 0.5rem (8px) marginTop after the open class is added to the container
        total += child.scrollHeight + 8;
        return total;
      }, 0);
      containerRef.current.style.setProperty("--open-min-height", `${fullHeight - 8}px`);
      timer = window.setTimeout(() => {
        containerRef.current.classList.add("open");
      }, 150); // 150 matches the .15s on the CSS transition
    } else {
      window.clearTimeout(timer); // so we don't accidentally add "open" after mousing out
      if (containerRef.current.classList.contains("open")) {
        containerRef.current.classList.remove("open");
      }
    }

    return () => window.clearTimeout(timer);
  }, [isOpen]);

  const collapseChildren = (e?: React.MouseEvent<HTMLDivElement>) => {
    if ((containerRef.current && !e) || (e && (e.target as HTMLElement).classList.contains("qes-alerts-container"))) {
      let indexHeight = 0;
      let zIndex = containerRef.current.children.length + 2;
      let index = 0;
      for (const item of containerRef.current.children) {
        const el = item as HTMLElement;
        el.style.top = `${indexHeight}px`;
        if (el.classList.contains("danger") && containerRef.current.children.item(index + 1) && containerRef.current.children.item(index + 1).classList.contains("danger")) {
          indexHeight += el.scrollHeight + 8;
        } else {
          indexHeight += 16;
        }
        el.style.zIndex = `${zIndex}`;
        zIndex -= 1;
        index += 1;
      }
      containerRef.current.classList.add("loaded");
    }
  };

  const setAlertTopProperties = () => {
    let index = 0;
    let numDanger = 0;
    let height = 0;
    for (const item of containerRef.current.children) {
      const el = item as HTMLElement;
      if (el.classList.contains("danger")) {
        numDanger += 1;
      }
      // we're basically cancelling out the margins we add after the initial opening (via scss)
      const margin = parseFloat(window.getComputedStyle(el).marginTop) * index;
      el.style.setProperty("--alert-top", `${el.offsetTop - margin + (index > 0 ? 8 * index : 0)}px`);
      index += 1;
    }
    height = (containerRef.current.children.length - (numDanger + 1)) * 16 + numDanger * 42;
    if (numDanger === 0) {
      height += containerRef.current.children[0].scrollHeight;
    }
    // It appears the css max function doesn't work with undefined custom properties, so the container would flash to height 0 on first hover
    // defining this here prevents it, even if the definition is temporary
    // The Math.max is to prevent it from being reset to the minimum, and the || 0 is because if the value isn't defined yet it returns ""
    containerRef.current.style.setProperty("--open-min-height", `${Math.max(parseFloat(containerRef.current.style.getPropertyValue("--open-min-height")) || 0, height)}px`);
    containerRef.current.style.setProperty("--closed-min-height", `${height}px`);
    containerRef.current.style.minHeight = "var(--closed-min-height)";
  };

  const calculateSpacing = () => {
    setAlertTopProperties();
    collapseChildren();
  };

  const handleMouseEnter = useMemo(() => () => setIsOpen(true), []);
  const handleMouseLeave = useMemo(() => () => setIsOpen(false), []);

  // Since the children (QESAlerts) can remove themselves from the DOM, we need to know when they do that so we can recalculate
  const children = Children.map(props.children, (child: React.ReactElement<QESAlertProps>) => {
    const newProps = { ...child.props };
    newProps.onClose = calculateSpacing;
    const updatedChild = { ...child, props: newProps };
    return updatedChild;
  });

  return (
    <div className={`qes-alerts-container${props.containerClass ? ` ${props.containerClass}` : ""}`} ref={containerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
    </div>
  );
};
